import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import SignaturePad from './SignaturePad';

const { FiX, FiCheck, FiCheckCircle, FiPenTool, FiDownload } = FiIcons;

const ProposalViewer = ({ proposal, onClose }) => {
  const { businessSettings, updateProposal } = useData();
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [showSignature, setShowSignature] = useState(false);
  const [signature, setSignature] = useState(null);
  const [clientInfo, setClientInfo] = useState({
    name: '',
    title: '',
    date: new Date().toISOString().split('T')[0]
  });

  if (!proposal) return null;

  const handleOptionSelect = (optionId) => {
    setSelectedOption(optionId);
  };

  const toggleAddOn = (optionId, addOnId) => {
    setSelectedAddOns(prev => ({
      ...prev,
      [optionId]: {
        ...prev[optionId],
        [addOnId]: !prev[optionId]?.[addOnId]
      }
    }));
  };

  const calculateOptionTotal = (option) => {
    const basePrice = option.price || 0;
    const addOnTotal = (selectedAddOns[option.id] ? 
      Object.entries(selectedAddOns[option.id])
        .filter(([_, selected]) => selected)
        .reduce((sum, [addOnId]) => {
          const addOn = proposal.availableAddOns?.find(a => a.id === parseInt(addOnId));
          return sum + (addOn ? addOn.price : 0);
        }, 0) : 0);
    return basePrice + addOnTotal;
  };

  const handleSignProposal = () => {
    if (selectedOption && signature && clientInfo.name) {
      const selectedAddOnsList = selectedAddOns[selectedOption] ? 
        Object.entries(selectedAddOns[selectedOption])
          .filter(([_, selected]) => selected)
          .map(([addOnId]) => parseInt(addOnId)) : [];

      updateProposal(proposal.id, {
        selectedOption,
        selectedAddOns: selectedAddOnsList,
        status: 'Signed',
        signedAt: new Date(),
        signature,
        clientInfo,
        finalAmount: calculateOptionTotal(proposal.options.find(opt => opt.id === selectedOption))
      });
      
      onClose();
    }
  };

  const getTemplateStyles = () => {
    const { template, accentColor, headerStyle } = proposal.designSettings || {};
    
    const templates = {
      modern: {
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        cardStyle: 'backdrop-blur-sm bg-white/90 shadow-xl',
        headerClass: 'text-center py-8'
      },
      classic: {
        background: '#ffffff',
        cardStyle: 'bg-white shadow-lg border-2 border-gray-200',
        headerClass: 'border-b-4 border-gray-800 py-6'
      },
      minimal: {
        background: '#fafafa',
        cardStyle: 'bg-white shadow-sm border border-gray-100',
        headerClass: 'py-4 border-b border-gray-100'
      },
      corporate: {
        background: 'linear-gradient(to bottom, #1e3c72, #2a5298)',
        cardStyle: 'bg-white shadow-2xl',
        headerClass: 'bg-gray-800 text-white py-8'
      }
    };

    return templates[template] || templates.modern;
  };

  const styles = getTemplateStyles();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto"
          style={{ background: styles.background }}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-6 ${styles.headerClass}`}>
            <div className="flex items-center space-x-4">
              {proposal.designSettings?.showCompanyLogo && (
                <img
                  src={businessSettings.logo}
                  alt={businessSettings.companyName}
                  className="h-12 w-auto object-contain"
                />
              )}
              <div>
                <h2 
                  className="text-2xl font-bold"
                  style={{ color: proposal.designSettings?.accentColor || businessSettings.primaryColor }}
                >
                  {proposal.title}
                </h2>
                <p className="text-gray-600">For: {proposal.client}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <SafeIcon icon={FiDownload} />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <SafeIcon icon={FiX} />
              </motion.button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6" style={{ fontFamily: businessSettings.fontFamily }}>
            {/* Company Info */}
            <div className={`mb-8 p-6 rounded-lg ${styles.cardStyle}`}>
              <h3 
                className="text-lg font-semibold mb-2" 
                style={{ color: proposal.designSettings?.accentColor || businessSettings.primaryColor }}
              >
                {businessSettings.companyName}
              </h3>
              <div className="text-sm text-gray-600 space-y-1">
                <p>{businessSettings.address}</p>
                <p>{businessSettings.phone}</p>
                <p>{businessSettings.email}</p>
              </div>
            </div>

            {/* Proposal Description */}
            {proposal.description && (
              <div className="mb-8">
                <h3 
                  className="text-lg font-semibold mb-3" 
                  style={{ color: proposal.designSettings?.accentColor || businessSettings.primaryColor }}
                >
                  Project Overview
                </h3>
                <p className="text-gray-700 leading-relaxed">{proposal.description}</p>
              </div>
            )}

            {/* Options */}
            <div className="mb-8">
              <h3 
                className="text-lg font-semibold mb-4" 
                style={{ color: proposal.designSettings?.accentColor || businessSettings.primaryColor }}
              >
                Choose Your Option
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {proposal.options?.map((option) => (
                  <motion.div
                    key={option.id}
                    whileHover={{ scale: 1.02 }}
                    className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                      selectedOption === option.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-blue-300'
                    } ${styles.cardStyle}`}
                    onClick={() => handleOptionSelect(option.id)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <h4 className="text-xl font-semibold text-gray-800">{option.name}</h4>
                      {selectedOption === option.id ? (
                        <SafeIcon icon={FiCheckCircle} className="text-green-500 text-xl" />
                      ) : (
                        <div className="w-6 h-6 border-2 border-gray-300 rounded-full"></div>
                      )}
                    </div>

                    <p className="text-gray-600 mb-4">{option.description}</p>

                    <div className="mb-4">
                      <div 
                        className="text-2xl font-bold mb-2" 
                        style={{ color: proposal.designSettings?.accentColor || businessSettings.primaryColor }}
                      >
                        Base: ${option.price?.toLocaleString()}
                      </div>
                    </div>

                    {/* Add-Ons */}
                    {proposal.availableAddOns && proposal.availableAddOns.length > 0 && (
                      <div className="mb-4">
                        <h5 className="font-semibold text-gray-800 mb-2">Available Add-Ons:</h5>
                        <div className="space-y-2">
                          {proposal.availableAddOns.map((addOn) => (
                            <label key={addOn.id} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedAddOns[option.id]?.[addOn.id] || false}
                                onChange={() => toggleAddOn(option.id, addOn.id)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                disabled={selectedOption !== option.id}
                              />
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-700">{addOn.name}</div>
                                <div className="text-xs text-gray-500">{addOn.description}</div>
                              </div>
                              <div className="text-sm font-semibold text-gray-800">+${addOn.price}</div>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Features */}
                    {option.features && option.features.length > 0 && (
                      <div className="mb-4">
                        <h5 className="font-semibold text-gray-800 mb-2">Included Features:</h5>
                        <ul className="space-y-1">
                          {option.features.map((feature, index) => (
                            <li key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                              <SafeIcon icon={FiCheck} className="text-green-500 text-xs" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Total Price */}
                    <div className="border-t pt-4">
                      <div 
                        className="text-3xl font-bold" 
                        style={{ color: proposal.designSettings?.accentColor || businessSettings.primaryColor }}
                      >
                        Total: ${calculateOptionTotal(option).toLocaleString()}
                      </div>
                    </div>

                    {selectedOption === option.id && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-4 p-3 bg-green-100 rounded-lg"
                      >
                        <p className="text-green-800 text-sm font-medium">
                          ✓ Option selected! Please sign below to accept this proposal.
                        </p>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Signature Section */}
            {selectedOption && (
              <div className={`mb-8 p-6 rounded-lg ${styles.cardStyle}`}>
                <h3 
                  className="text-lg font-semibold mb-4" 
                  style={{ color: proposal.designSettings?.accentColor || businessSettings.primaryColor }}
                >
                  Digital Signature
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={clientInfo.name}
                      onChange={(e) => setClientInfo(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title/Position
                    </label>
                    <input
                      type="text"
                      value={clientInfo.title}
                      onChange={(e) => setClientInfo(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your title or position"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Signature *
                  </label>
                  <SignaturePad onSignature={setSignature} />
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Date: {new Date().toLocaleDateString()}
                  </div>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSignProposal}
                    disabled={!signature || !clientInfo.name}
                    className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <SafeIcon icon={FiPenTool} />
                    <span>Sign & Accept Proposal</span>
                  </motion.button>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="border-t border-gray-200 pt-6 text-center text-sm text-gray-500">
              <p>Thank you for considering {businessSettings.companyName}</p>
              <p>Questions? Contact us at {businessSettings.email} or {businessSettings.phone}</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProposalViewer;